import queryString from 'query-string'
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import type { IDonViTinh, IDonViTinhImportRow, IDonViTinhPayload, TrangThaiDonViTinh } from '@/interfaces/donViTinhInterfaces'

const PREFIX = '/don-vi-tinh'

function mapDonViTinhFromApi(raw: Record<string, unknown>): IDonViTinh {
  const trangThai = raw.trangThai
  return {
    id: String(raw.id ?? ''),
    maDonViTinh: String(raw.maDonViTinh ?? ''),
    tenDonViTinh: String(raw.tenDonViTinh ?? ''),
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    trangThai:
      trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong' ? trangThai : 'dangHoatDong',
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

function toPayload(body: IDonViTinhPayload) {
  const payload: Record<string, unknown> = { tenDonViTinh: body.tenDonViTinh.trim() }
  if (body.maDonViTinh?.trim()) payload.maDonViTinh = body.maDonViTinh.trim()
  if (body.ghiChu?.trim()) payload.ghiChu = body.ghiChu.trim()
  return payload
}

export function donViTinhListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  const maDonViTinh = q.maDonViTinh
  if (typeof maDonViTinh === 'string' && maDonViTinh.trim()) out.maDonViTinh = maDonViTinh.trim()
  const tenDonViTinh = q.tenDonViTinh
  if (typeof tenDonViTinh === 'string' && tenDonViTinh.trim()) out.tenDonViTinh = tenDonViTinh.trim()
  const trangThai = q.trangThai
  if (trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong') out.trangThai = trangThai
  return out
}

export function fetchDanhSachDonViTinh(search: string): Promise<PagedListResult<IDonViTinh>> {
  const params = donViTinhListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-don-vi-tinh`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: (data as Record<string, unknown>[]).map(mapDonViTinhFromApi),
        paging,
      }
    }),
  )
}

export function fetchChiTietDonViTinh(id: string | number): Promise<IDonViTinh> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-don-vi-tinh`).then((res: unknown) =>
      mapDonViTinhFromApi(normalizeDetailResponse(res) as Record<string, unknown>),
    ),
  )
}

export function taoDonViTinh(body: IDonViTinhPayload) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-don-vi-tinh`, toPayload(body)))
}

export function capNhatDonViTinh(id: string | number, body: IDonViTinhPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-don-vi-tinh`, toPayload(body)))
}

export function xoaDonViTinh(id: string | number) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-don-vi-tinh`))
}

export type XoaNhieuDonViTinhResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều đơn vị tính — gọi API xóa từng id (loại trùng id). */
export async function xoaNhieuDonViTinh(ids: string[]): Promise<XoaNhieuDonViTinhResult> {
  const uniqueIds = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)))
  const failed: XoaNhieuDonViTinhResult['failed'] = []
  let ok = 0
  for (const id of uniqueIds) {
    try {
      await xoaDonViTinh(id)
      ok += 1
    } catch (e) {
      failed.push({
        id,
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiDonViTinh(id: string | number, trangThai: TrangThaiDonViTinh) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-don-vi-tinh`, { trangThai }))
}

export type NhapDanhSachDonViTinhResult = {
  success: boolean
  message: string
  created?: number
  updated?: number
  data?: unknown[]
}

export function nhapDanhSachDonViTinh(rows: IDonViTinhImportRow[]) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/don-vi-tinh-nhap-danh-sach`, rows))
}
